<template>
	<section class="journal-page appointments-typography" :class="{ 'journal-page--autumn': !isEditMode, 'is-art-pending': isArtPending }" aria-label="Appointments">
		<header v-if="isEditMode" class="journal-header">
			<span class="edit-mode-pill">Edit mode</span>
			<div class="journal-actions">
				<button
					class="noc-back-btn"
					type="button"
					@click="exitEditMode"
				>
					<AppointmentIcon name="exit" class="btn-icon" />
					Exit edit mode
				</button>
			</div>
		</header>
		<div v-if="!isLoading && !appointmentsJournal" class="card placeholder">
			Appointments journal is not available.
		</div>
		<template v-else>
			<AppointmentsWeekView
				ref="weekViewRef"
				v-show="!isEditMode"
				:journal="appointmentsJournal"
				v-model:summary-mode="isSummaryMode"
				:initial-week-start="initialWeekStart"
				:initial-date-from="initialDateFrom"
				:initial-date-to="initialDateTo"
				:initial-supplier-id="initialSupplierId"
				:source-supplier-invoice-id="sourceSupplierInvoiceId"
				:source-supplier-invoice-document-id="sourceSupplierInvoiceDocumentId"
				:handoff-key="handoffKey"
				@range-change="handleRangeChange"
				@week-loaded="handleWeekLoaded"
				@reveal="weekRevealed = true"
			>
				<template #toolbar-actions>
					<div class="journal-controls">
						<button
							class="noc-back-btn secondary week-nav-btn"
							type="button"
							aria-label="Previous week"
							title="Previous week"
							@click="weekViewRef?.changeWeek(-7)"
							:disabled="!weekViewRef || weekViewRef.isLoading"
						>
							<AppointmentIcon name="chevron-left" class="btn-icon" /><span class="week-nav-label">Prev week</span>
						</button>
						<button
							class="noc-back-btn secondary week-nav-btn"
							type="button"
							aria-label="Next week"
							title="Next week"
							@click="weekViewRef?.changeWeek(7)"
							:disabled="!weekViewRef || weekViewRef.isLoading"
						>
							<span class="week-nav-label">Next week</span><AppointmentIcon name="chevron-right" class="btn-icon" />
						</button>
						<DateRangePicker use-fixed animated
							v-if="weekViewRef"
							:disabled="weekViewRef.isLoading"
							:dateFrom="weekViewRef.activeDateFrom"
							:dateTo="weekViewRef.activeDateTo"
							placeholder="Select date"
							@update="weekViewRef.handleDatePickerUpdate"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" /></template>
							<template #previous-icon><AppointmentIcon name="chevron-left" /></template>
							<template #next-icon><AppointmentIcon name="chevron-right" /></template>
						</DateRangePicker>
					</div>
				</template>
				<template #tab-actions>
					<button
						class="outline-btn modal-btn"
						type="button"
						@click="openTemplatesModal"
						:disabled="!appointmentsJournal"
					>
						<AppointmentIcon name="templates" class="btn-icon" />
						Templates
					</button>
					<button
						class="outline-btn modal-btn"
						type="button"
						@click="emit('open-live-map')"
					>
						<AppointmentIcon name="map" class="btn-icon" />
						Live map
					</button>
				</template>
			</AppointmentsWeekView>
			<AppointmentsTemplateEditor
				v-show="isEditMode"
				:template="selectedTemplate"
			/>
		</template>
	</section>

	<div
		v-if="showTemplatesModal"
		class="templates-modal-overlay appointments-typography"
		role="dialog"
		aria-modal="true"
		aria-label="Templates"
		@click="closeTemplatesModal"
	>
		<div class="templates-modal" @click.stop>
			<header class="templates-modal-header">
				<div class="templates-modal-title">
					<h3>Templates</h3>
					<p>Apply appointments to the current week or edit a template.</p>
				</div>
				<div class="templates-modal-actions">
					<span v-if="isEditMode" class="edit-mode-pill">Edit mode</span>
				</div>
			</header>
			<div class="templates-modal-body">
				<div class="template-card">
					<div class="template-card-body">
						<div class="template-row">
							<TemplateSelect
								v-model="selectedTemplateId"
								:options="templates"
								:disabled="isLoadingTemplates"
							/>
							<button
								class="primary-btn modal-btn"
								type="button"
								@click="handleApplyTemplate"
								:disabled="!canApply"
							>
								<AppointmentIcon name="check" v-if="!isApplyingTemplate" class="btn-icon" />
								{{ isApplyingTemplate ? 'Applying…' : 'Apply to week' }}
							</button>
						</div>
						<div class="template-actions-row">
							<button
								class="danger-btn modal-btn"
								type="button"
								@click="handleDeleteTemplate"
								:disabled="!selectedTemplateId"
							>
								<AppointmentIcon name="trash" class="btn-icon" />
								{{ deleteButtonLabel }}
							</button>
							<button
								class="outline-btn modal-btn"
								type="button"
								@click="toggleEditMode"
								:disabled="!selectedTemplateId"
							>
								<AppointmentIcon name="exit" v-if="isEditMode" class="btn-icon" />
								<AppointmentIcon name="edit" v-else class="btn-icon" />
								{{ isEditMode ? 'Exit edit mode' : 'Edit template' }}
							</button>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button class="ghost-btn modal-btn" type="button" @click="handleCreateTemplate">
						<AppointmentIcon name="plus" class="btn-icon" />
						New template
					</button>
					<div class="footer-actions">
						<button class="outline-btn close-btn modal-btn" type="button" @click="closeTemplatesModal">
							<AppointmentIcon name="close" class="btn-icon" />
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useJournalStore } from '@/stores/journal';
import { useJournalAppointmentTemplatesStore } from '@/stores/journalAppointmentTemplates';
import { useJournalPurchasesStore } from '@/stores/journalPurchases';
import { useClientInvoicesStore } from '@/stores/clientInvoices';
import { useAnalyticsReportsStore } from '@/stores/analyticsReports';
import AppointmentsWeekView from '@/components/journal/AppointmentsWeekView.vue';
import AppointmentsTemplateEditor from '@/components/journal/AppointmentsTemplateEditor.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import TemplateSelect from '@/components/journal/TemplateSelect.vue';
import '@/assets/styles/appointments-typography.css';
import autumnArtUrl from '@/assets/images/autumn-dragonstone-corner.webp';
import { decodeImage } from '@/utils/pageReveal';

const props = defineProps({
	initialWeekStart: {
		type: [String, Date],
		default: null,
	},
	initialDateFrom: {
		type: String,
		default: null,
	},
	initialDateTo: {
		type: String,
		default: null,
	},
	initialSupplierId: {
		type: [String, Number],
		default: '',
	},
	sourceSupplierInvoiceId: {
		type: [String, Number],
		default: null,
	},
	sourceSupplierInvoiceDocumentId: {
		type: String,
		default: null,
	},
	initialSummaryMode: {
		type: Boolean,
		default: false,
	},
	summaryModeKey: {
		type: [String, Number],
		default: null,
	},
	handoffKey: {
		type: [String, Number],
		default: null,
	},
});

// Today's jobs on a map of London open in their own page.
const emit = defineEmits(['open-live-map']);

const journalStore = useJournalStore();
const templatesStore = useJournalAppointmentTemplatesStore();
const purchasesStore = useJournalPurchasesStore();
const clientInvoicesStore = useClientInvoicesStore();
const analyticsReportsStore = useAnalyticsReportsStore();
const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;
const weekRange = ref('');
const selectedTemplateId = ref('');
const isEditMode = ref(false);
const isApplyingTemplate = ref(false);
const isSummaryMode = ref(false);
const weekViewRef = ref(null);
// The corner art fades in with the first week once it has decoded, instead of drawing in as it downloads.
const artDecoded = ref(false);
const weekRevealed = ref(false);
const isArtPending = computed(() => !artDecoded.value || !weekRevealed.value);
decodeImage(autumnArtUrl).then(() => {
	artDecoded.value = true;
});
const initializingJournal = ref(!journalStore.initialized);
const showTemplatesModal = ref(false);
const deleteConfirm = ref(false);
const deleteConfirmId = ref('');
const prefetchScheduled = ref(false);
const prefetchTriggered = ref(false);
let deleteConfirmTimer = null;
let escapeListenerBound = false;
let prefetchTimer = null;
let prefetchIdleHandle = null;

onMounted(async () => {
	try {
		await journalStore.initialize?.();
	} catch (err) {
		logError(err, 'AppointmentsJournalView initialize');
	} finally {
		initializingJournal.value = false;
	}
	try {
		await templatesStore.fetchTemplates();
	} catch (err) {
		logError(err, 'AppointmentsJournalView fetchTemplates');
	}
});

watch(
	() => props.initialSummaryMode,
	(next) => {
		if (typeof next === 'boolean') {
			isSummaryMode.value = next;
		}
	},
	{ immediate: true },
);

watch(
	() => props.summaryModeKey,
	(next) => {
		if (next !== null && next !== undefined) {
			isSummaryMode.value = true;
		}
	},
);

const waitForInvoiceLists = async (timeoutMs = 4000) => {
	const start = Date.now();
	while (clientInvoicesStore.listsLoading && !clientInvoicesStore.lists.length) {
		if (Date.now() - start > timeoutMs) {
			break;
		}
		await new Promise((resolve) => setTimeout(resolve, 50));
	}
};

const prefetchSupportingData = async () => {
	const tasks = [];
	if (!purchasesStore.initialized && !purchasesStore.isLoading) {
		tasks.push({
			label: 'purchases',
			promise: purchasesStore.initialize(),
		});
	}
	if (!clientInvoicesStore.lists.length && !clientInvoicesStore.listsLoading) {
		tasks.push({
			label: 'client invoices',
			promise: clientInvoicesStore.initialize(),
		});
	}
	if (!analyticsReportsStore.reports.length && !analyticsReportsStore.isLoading) {
		tasks.push({
			label: 'analytics',
			promise: analyticsReportsStore.fetchReports({ page: 1 }),
		});
	}

	if (tasks.length) {
		const results = await Promise.allSettled(tasks.map((task) => task.promise));
		results.forEach((result, index) => {
			if (result.status === 'rejected') {
				logError?.(result.reason, `AppointmentsJournalView prefetch ${tasks[index].label}`);
			}
		});
	}

	if (clientInvoicesStore.listsLoading && !clientInvoicesStore.lists.length) {
		await waitForInvoiceLists();
	}

	const listOptions = clientInvoicesStore.listOptions || [];
	const listIds = listOptions.length
		? listOptions.map((list) => list.id)
		: (clientInvoicesStore.activeListId ? [clientInvoicesStore.activeListId] : []);

	for (const listId of listIds) {
		const existingItems = clientInvoicesStore.itemsByListId?.[listId] || [];
		const isItemsLoading = clientInvoicesStore.itemsLoadingByListId?.[listId];
		const hasPagination = Boolean(clientInvoicesStore.paginationByListId?.[listId]);
		if ((existingItems.length || hasPagination) || isItemsLoading) {
			continue;
		}
		try {
			await clientInvoicesStore.fetchItemsForList(listId);
		} catch (err) {
			logError?.(err, 'AppointmentsJournalView prefetch invoice items');
		}
	}
};

const schedulePrefetch = () => {
	if (prefetchTriggered.value || prefetchScheduled.value) return;
	prefetchScheduled.value = true;
	const runPrefetch = () => {
		if (prefetchTriggered.value) return;
		prefetchScheduled.value = false;
		prefetchTriggered.value = true;
		if (prefetchTimer) {
			clearTimeout(prefetchTimer);
			prefetchTimer = null;
		}
		if (prefetchIdleHandle && typeof window !== 'undefined' && window.cancelIdleCallback) {
			window.cancelIdleCallback(prefetchIdleHandle);
			prefetchIdleHandle = null;
		}
		void prefetchSupportingData();
	};
	if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
		prefetchIdleHandle = window.requestIdleCallback(
			() => {
				prefetchIdleHandle = null;
				runPrefetch();
			},
			{ timeout: 1000 },
		);
	}
	prefetchTimer = window.setTimeout(() => {
		prefetchTimer = null;
		runPrefetch();
	}, 0);
};

const handleEscape = (event) => {
	if (event.key === 'Escape' && showTemplatesModal.value) {
		closeTemplatesModal();
	}
};

const resetDeleteConfirm = () => {
	deleteConfirm.value = false;
	deleteConfirmId.value = '';
	if (deleteConfirmTimer) {
		clearTimeout(deleteConfirmTimer);
		deleteConfirmTimer = null;
	}
};

const openTemplatesModal = () => {
	showTemplatesModal.value = true;
	if (!escapeListenerBound) {
		window.addEventListener('keydown', handleEscape);
		escapeListenerBound = true;
	}
};

const closeTemplatesModal = () => {
	showTemplatesModal.value = false;
	resetDeleteConfirm();
	if (escapeListenerBound) {
		window.removeEventListener('keydown', handleEscape);
		escapeListenerBound = false;
	}
};

onBeforeUnmount(() => {
	if (escapeListenerBound) {
		window.removeEventListener('keydown', handleEscape);
	}
	if (prefetchTimer) {
		clearTimeout(prefetchTimer);
		prefetchTimer = null;
	}
	if (prefetchIdleHandle && typeof window !== 'undefined' && window.cancelIdleCallback) {
		window.cancelIdleCallback(prefetchIdleHandle);
		prefetchIdleHandle = null;
	}
	resetDeleteConfirm();
});

const normalizeId = (value) => {
	if (value === null || value === undefined) return '';
	return String(value);
};

const normalizedName = (name = '') => name.trim().toLowerCase();
const isLoading = computed(() => initializingJournal.value || journalStore.loading);
const appointmentsJournal = computed(() => {
	return journalStore.journalList.find(
		(journal) => normalizedName(journal.name) === 'appointments',
	) || null;
});
const templates = computed(() => templatesStore.templateOptions || []);
const isLoadingTemplates = computed(() => templatesStore.isLoadingTemplates);
const selectedTemplate = computed(
	() =>
		templates.value.find(
			(template) => normalizeId(template.id) === normalizeId(selectedTemplateId.value),
		) || null,
);
const canApply = computed(
	() =>
		Boolean(appointmentsJournal.value?.id) &&
		Boolean(selectedTemplateId.value) &&
		!isEditMode.value &&
		!isApplyingTemplate.value,
);

watch(
	appointmentsJournal,
	(journal) => {
		if (journal?.id) {
			journalStore.setActiveJournal(journal.id);
		}
	},
	{ immediate: true },
);

watch(
	templates,
	(nextTemplates) => {
		if (!selectedTemplateId.value) return;
		const exists = nextTemplates.some(
			(template) => normalizeId(template.id) === normalizeId(selectedTemplateId.value),
		);
	if (!exists) {
		selectedTemplateId.value = '';
		isEditMode.value = false;
		resetDeleteConfirm();
	}
},
{ deep: true },
);

watch(
	() => selectedTemplateId.value,
	() => {
		resetDeleteConfirm();
	},
);

const handleRangeChange = (value) => {
	weekRange.value = value || '';
};

const handleWeekLoaded = () => {
	schedulePrefetch();
};

const titleSuffix = computed(() => {
	if (isEditMode.value) return '';
	if (isSummaryMode.value) {
		return weekRange.value ? `summary for ${weekRange.value}` : 'summary';
	}
	return weekRange.value ? `for ${weekRange.value}` : '';
});

const handleApplyTemplate = async () => {
	if (!weekViewRef.value || !selectedTemplateId.value || isApplyingTemplate.value) {
		return;
	}
	isApplyingTemplate.value = true;
	try {
		await weekViewRef.value.applyTemplateToWeek(selectedTemplateId.value);
		resetDeleteConfirm();
		closeTemplatesModal();
	} catch (err) {
		logError(err, 'AppointmentsJournalView handleApplyTemplate');
		toast?.error?.('Failed to apply template.');
	} finally {
		isApplyingTemplate.value = false;
	}
};

const toggleEditMode = () => {
	if (!selectedTemplateId.value) {
		toast?.info?.('Select a template to edit.');
		return;
	}
	isEditMode.value = !isEditMode.value;
	resetDeleteConfirm();
	if (isEditMode.value) {
		closeTemplatesModal();
	}
};

const exitEditMode = () => {
	isEditMode.value = false;
	resetDeleteConfirm();
	closeTemplatesModal();
};

const handleCreateTemplate = async () => {
	const name = window.prompt('Template name');
	if (!name) return;
	try {
		const created = await templatesStore.createTemplate({ name });
		selectedTemplateId.value = normalizeId(created.id);
		isEditMode.value = true;
		resetDeleteConfirm();
		closeTemplatesModal();
		toast?.success?.('Template created.');
	} catch (err) {
		logError(err, 'AppointmentsJournalView handleCreateTemplate');
		toast?.error?.(templatesStore.templatesError || 'Failed to create template.');
	}
};

const handleDeleteTemplate = async () => {
	if (!selectedTemplate.value) return;
	const templateId = normalizeId(selectedTemplate.value.id);
	if (!deleteConfirm.value || deleteConfirmId.value !== templateId) {
		deleteConfirm.value = true;
		deleteConfirmId.value = templateId;
		if (deleteConfirmTimer) {
			clearTimeout(deleteConfirmTimer);
		}
		deleteConfirmTimer = setTimeout(() => {
			resetDeleteConfirm();
		}, 4000);
		return;
	}
	const documentId = selectedTemplate.value.documentId || selectedTemplate.value.id;
	if (!documentId) return;
	try {
		await templatesStore.deleteTemplate(documentId);
		toast?.success?.('Template deleted.');
		selectedTemplateId.value = '';
		isEditMode.value = false;
		resetDeleteConfirm();
	} catch (err) {
		logError(err, 'AppointmentsJournalView handleDeleteTemplate');
		toast?.error?.(templatesStore.templatesError || 'Failed to delete template.');
		resetDeleteConfirm();
	}
};

const deleteButtonLabel = computed(() =>
	deleteConfirm.value ? 'Click again to delete' : 'Delete template',
);
</script>

<style scoped>
.noc-back-btn:not(.secondary):hover:not(:disabled) {
	background-color: var(--primary-hover, #0855b3);
	filter: none;
}

button,
.noc-back-btn {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	font: var(--ap-type-control);
}

.journal-page {
	--box-shadow: none;
	background-color: var(--noc-background);
	display: flex;
	flex-direction: column;
	padding: 24px;
	height: 100%;
	box-sizing: border-box;
	overflow: auto;
}

/* A white veil over the art that can be faded out; a registered property can transition. */
@property --autumn-veil {
	syntax: '<number>';
	inherits: false;
	initial-value: 0;
}

.journal-page--autumn {
	background-image:
		linear-gradient(rgb(255 255 255 / var(--autumn-veil)), rgb(255 255 255 / var(--autumn-veil))),
		linear-gradient(180deg, rgb(255 255 255 / 5%) 0, rgb(255 255 255 / 12%) 76px, #fff 230px),
		url('@/assets/images/autumn-dragonstone-corner.webp');
	background-position: right top 45px;
	background-size: 100% 100%, 100% 100%, clamp(460px, 48vw, 620px) auto;
	background-repeat: no-repeat;
	transition: --autumn-veil 1100ms cubic-bezier(0.33, 1, 0.68, 1) 120ms;
}

.journal-page--autumn.is-art-pending {
	--autumn-veil: 1;
}

@media (prefers-reduced-motion: reduce) {
	.journal-page--autumn {
		transition: none;
	}
}

.journal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
	gap: 16px;
	flex-wrap: wrap;
	min-height: 40px;
}

/* The week navigation stays on one line; the header grid moves it as a whole. */
.journal-controls {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: nowrap;
}

.journal-controls > * {
	flex-shrink: 0;
}

.journal-controls :deep(.drp-trigger-text) {
	white-space: nowrap;
}

/* Tablets: the arrows alone step through weeks, leaving the row to the view switch.
   Touch screens fold the sidebar into a 12px strip, so the page there is 208px wider. */
@media (max-width: 1279px) and (hover: hover), (max-width: 1279px) and (pointer: fine), (max-width: 1071px) {
	.week-nav-label {
		display: none;
	}

	.journal-controls .week-nav-btn {
		width: 36px;
		padding: 0;
		justify-content: center;
	}
}

@media (max-width: 650px) {
	.journal-controls {
		flex-wrap: wrap;
	}
}

.journal-controls-sep {
	display: inline-flex;
	align-items: center;
	color: var(--noc-grey-dark, #9aa0a6);
	padding: 0 4px;
	font-size: 1.2rem;
	line-height: 1;
}

.journal-controls :deep(.drp-trigger) {
	color: var(--text-color);
	border-radius: 8px;
	box-shadow: none;
	width: 260px;
	border-color: var(--border-color);
}

.journal-controls :deep(.drp-trigger:hover:not(:disabled)) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.journal-controls :deep(.drp-trigger--active) {
	border-color: var(--border-color);
}

.journal-controls :deep(.date-range-picker button),
.journal-controls :deep(.drp-cell-num) {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.journal-controls :deep(.drp-nav-btn:hover:not(:disabled)),
.journal-controls :deep(.drp-month-title--clickable:hover:not(:disabled)),
.journal-controls :deep(.drp-preset-btn:hover:not(:disabled)),
.journal-controls :deep(.drp-month-cell:not(.drp-month-cell--selected):hover:not(:disabled)),
.journal-controls :deep(.drp-btn--cancel:hover:not(:disabled)),
.journal-controls :deep(.drp-cell:not(.drp-cell--empty):not(.drp-cell--selected):hover:not(:disabled) .drp-cell-num) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.journal-controls :deep(.drp-btn--update:hover:not(:disabled)),
.journal-controls :deep(.drp-month-cell--selected:hover:not(:disabled)),
.journal-controls :deep(.drp-cell--selected:hover:not(:disabled) .drp-cell-num) {
	background-color: var(--primary-hover, #0855b3);
	border-color: var(--primary-hover, #0855b3);
	color: #fff;
}

.journal-controls :deep(.drp-preset-btn:not(.drp-preset-btn--active):hover:not(:disabled)) {
	color: #444;
}

.edit-mode-pill {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 4px 10px;
	border-radius: 999px;
	color: var(--noc-blue, #0065ff);
	border: 1px solid var(--noc-blue-10, rgba(0, 101, 255, 0.2));
	background: var(--noc-blue-05, rgba(0, 101, 255, 0.08));
	font: var(--ap-type-label);
}

.journal-range {
	font: inherit;
	color: inherit;
}

.journal-page .noc-back-btn {
	border-radius: 8px;
	box-shadow: none;
}

.noc-back-btn.secondary {
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	border: 1px solid var(--border-color);
	height: 34px;
	padding: 0 12px;
	border-radius: 8px;
	font: var(--ap-type-control);
}

.noc-back-btn.secondary.active {
	background: var(--noc-blue-05, rgba(0, 101, 255, 0.08));
	border-color: var(--noc-blue-10, rgba(0, 101, 255, 0.2));
	color: var(--noc-blue, #0065ff);
	font-weight: 500;
}

.noc-back-btn.secondary:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
	filter: none;
}

.outline-btn {
	border: 1px solid var(--border-color, #ccc);
	background: transparent;
	border-radius: 4px;
	padding: 6px 14px;
	cursor: pointer;
}

.card.placeholder {
	border: 1px dashed var(--border-color, #d6d6d6);
	border-radius: 8px;
	padding: 32px;
	text-align: center;
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
}

.templates-modal-overlay {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
	backdrop-filter: blur(3px);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	z-index: 2000;
}

.templates-modal {
	width: min(660px, 100%);
	background: var(--noc-background, #fff);
	border-radius: 8px;
	border: 1px solid var(--border-color, #dcdcdc);
	box-shadow: 0 30px 80px rgba(15, 23, 42, 0.2);
	overflow: visible;
	display: flex;
	flex-direction: column;
	animation: modal-rise 0.2s ease-out;
}

.templates-modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 20px 24px;
}

.templates-modal-title h3 {
	margin: 0;
	font: var(--ap-type-modal-title);
	letter-spacing: normal;
}

.templates-modal-title p {
	margin: 4px 0 0;
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

.templates-modal-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.templates-modal-body {
	display: grid;
	gap: 56px;
	padding: 36px 24px 24px;
}

.template-card {
	border: none;
	border-radius: 0;
	padding: 0;
	background: transparent;
	display: grid;
	gap: 12px;
}

.template-card-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.template-card-header h4 {
	margin: 0;
	font: var(--ap-type-section);
}

.template-card-header p {
	margin: 4px 0 0;
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

.template-card-body {
	display: grid;
	gap: 12px;
}

.field-label {
	color: var(--text-color-secondary);
	font: var(--ap-type-label);
}

.template-row {
	display: grid;
	grid-template-columns: minmax(220px, 1fr) auto;
	gap: 12px;
	align-items: center;
}

.template-actions-row {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.primary-btn {
	background: var(--noc-blue, #0a61c9);
	color: #fff;
	border: none;
	border-radius: 8px;
	padding: 8px 16px;
	cursor: pointer;
	box-shadow: var(--box-shadow);
}

.primary-btn:hover:not(:disabled) {
	background: var(--primary-hover, #0855b3);
}

.close-btn {
	height: 32px;
	padding: 6px 14px;
}

.ghost-btn {
	border: none;
	background: var(--noc-blue-05, rgba(10, 97, 201, 0.08));
	color: var(--noc-blue, #0a61c9);
	border-radius: 999px;
	padding: 6px 14px;
	cursor: pointer;
}

.danger-btn {
	border: none;
	background: rgba(211, 47, 47, 0.12);
	color: var(--noc-red, #d32f2f);
	border-radius: 8px;
	padding: 8px 14px;
	cursor: pointer;
}

.modal-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	height: 36px;
	padding: 0 12px;
	border-radius: 8px;
	cursor: pointer;
	box-shadow: none;
	font: var(--ap-type-control);
}


.modal-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	box-shadow: none;
	filter: grayscale(0.5);
}

.outline-btn.modal-btn {
	border: 1px solid var(--border-color, #dcdcdc);
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
}

.outline-btn.modal-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.primary-btn.modal-btn {
	border: 1px solid var(--noc-blue, #0a61c9);
	background: var(--noc-blue, #0a61c9);
	color: #fff;
	box-shadow: none;
}

.primary-btn.modal-btn:hover:not(:disabled) {
	background: var(--primary-hover, #0855b3);
	border-color: var(--primary-hover, #0855b3);
}

.ghost-btn.modal-btn {
	border: 1px solid var(--noc-blue-15, rgba(10, 97, 201, 0.15));
	background: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	color: var(--noc-blue, #0a61c9);
}

.ghost-btn.modal-btn:hover:not(:disabled) {
	background-color: var(--noc-blue-10, rgba(10, 97, 201, 0.1));
	border-color: var(--noc-blue-30, rgba(10, 97, 201, 0.3));
}

.danger-btn.modal-btn {
	border: 1px solid rgba(211, 47, 47, 0.25);
	background: var(--noc-red-10, rgba(211, 47, 47, 0.1));
	color: var(--noc-red, #d32f2f);
}

.danger-btn.modal-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--noc-red, #d32f2f) 18%, transparent);
	border-color: color-mix(in srgb, var(--noc-red, #d32f2f) 35%, transparent);
}

.danger-btn.modal-btn:disabled {
	background: rgba(0, 0, 0, 0.05);
	color: var(--text-color-secondary, #999);
	border-color: transparent;
	opacity: 0.6;
}


.modal-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.footer-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

@keyframes modal-rise {
	from {
		transform: translateY(8px);
		opacity: 0.9;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

@media (max-width: 720px) {
	.templates-modal {
		width: 100%;
	}

	.templates-modal-header {
		flex-direction: column;
		align-items: flex-start;
	}

	.template-row {
		grid-template-columns: 1fr;
	}

	.template-card-header {
		flex-direction: column;
		align-items: flex-start;
	}
}
/* Calendar typography follows the same roles as the surrounding toolbar. */
.journal-controls :deep(.drp-trigger),
.journal-controls :deep(.drp-month-cell),
.journal-controls :deep(.drp-btn) {
	font: var(--ap-type-control);
}

.journal-controls :deep(.drp-month-title) {
	font: var(--ap-type-section);
}

.journal-controls :deep(.drp-cell-num),
.journal-controls :deep(.drp-preset-btn) {
	font: var(--ap-type-body);
}

.journal-controls :deep(.drp-weekday) {
	font: var(--ap-type-label);
	color: var(--text-color-secondary);
}

.journal-controls :deep(.drp-footer-range) {
	font: var(--ap-type-caption);
}

.journal-controls :deep(.drp-cell--selected .drp-cell-num),
.journal-controls :deep(.drp-month-cell--selected),
.journal-controls :deep(.drp-preset-btn--active) {
	font-weight: 500;
}

.journal-controls :deep(.drp-cell--today:not(.drp-cell--selected) .drp-cell-num) {
	font-weight: 700;
}

.journal-controls :deep(.drp-trigger-text),
.journal-controls :deep(.drp-cell-num),
.journal-controls :deep(.drp-footer-range) {
	font-variant-numeric: tabular-nums;
}

/* Frosted week controls sit over the corner artwork; their geometry stays shared with the journal. */
.journal-controls .noc-back-btn.secondary,
.journal-controls :deep(.drp-trigger) {
	height: 36px;
	--week-control-hover-tint: rgb(255 255 255 / 98%);
	--week-control-border: color-mix(in srgb, var(--text-color-secondary, #5e6170) 12%, transparent);
	background: rgb(255 255 255 / 94%);
	border-color: var(--week-control-border);
	box-shadow: 0 2px 8px rgb(67 51 28 / 7%);
	/* Keep the backdrop's geometry and compositing stable, including while pressed. */
	filter: none;
	transform: none;
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
	.journal-controls .noc-back-btn.secondary,
	.journal-controls :deep(.drp-trigger) {
		--week-control-hover-tint: rgb(255 255 255 / 28%);
		background-color: transparent;
		background-image: linear-gradient(135deg, rgb(255 255 255 / 76%), rgb(255 255 255 / 48%));
		-webkit-backdrop-filter: blur(16px) saturate(115%);
		backdrop-filter: blur(16px) saturate(115%);
	}
}

.journal-controls .noc-back-btn.secondary:hover:not(:disabled),
.journal-controls :deep(.drp-trigger:hover:not(:disabled)) {
	/* Do not reset background-image: gradients switch abruptly rather than fade. */
	background-color: var(--week-control-hover-tint);
	border-color: var(--week-control-border);
}

@media (max-width: 900px) {
	.journal-page--autumn {
		background-size: 100% 100%, 100% 100%, 460px auto;
	}
}

@media (max-width: 650px) {
	.journal-page--autumn {
		background-size: 100% 100%, 100% 100%, 360px auto;
	}
}

@media (prefers-reduced-transparency: reduce) {
	.journal-controls .noc-back-btn.secondary,
	.journal-controls :deep(.drp-trigger),
	.journal-controls .noc-back-btn.secondary:hover:not(:disabled),
	.journal-controls :deep(.drp-trigger:hover:not(:disabled)) {
		background: #fff;
		border-color: var(--week-control-border);
		-webkit-backdrop-filter: none;
		backdrop-filter: none;
	}
}
</style>
